/**
 * Schema-Verwaltung (F29–F31, nur tester:reviewer):
 * Struktur-Browser (schema.md je Datenbank), Änderungs-Historie
 * (Commits + persistierte KI-Analysen) und Schema-Backup-Läufe (F30).
 * Quelle ist das Forgejo-Schema-Repo — Kundennutzer sehen nichts davon (S8).
 */

import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ExternalLink, RefreshCw } from "lucide-react"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { Markdown } from "@/components/Markdown"
import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"

const SEKTION = "rounded-xl border bg-card p-5 shadow-card"
const TITEL = "mb-2.5 text-[13px] font-bold uppercase tracking-wide text-foreground/70"

export function AdminSchemaPage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  // Eingeschränkte Reviewer sehen die Änderungs-Historie nicht.
  const eingeschraenkt = useAuthStore((s) => s.isEingeschraenkt())
  const [tab, setTab] = useState<"struktur" | "historie" | "backup">("struktur")
  const [dbSlug, setDbSlug] = useState<string>(searchParams.get("db") ?? "")
  const [analyseName, setAnalyseName] = useState<string>("")
  const tabelleHinweis = searchParams.get("tabelle")

  // Instanz hat i. d. R. genau eine Anwendung — die erste aktive nehmen
  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const appId = anwendungen?.[0]?.id

  const { data: uebersicht, error: uebersichtFehler } = useQuery({
    queryKey: ["admin", "schema", appId],
    queryFn: () => api.admin.schema.uebersicht(appId!),
    enabled: appId !== undefined,
  })

  // Erste Datenbank vorwählen, sobald die Übersicht da ist
  useEffect(() => {
    if (!dbSlug && uebersicht?.datenbanken.length) {
      setDbSlug(uebersicht.datenbanken[0].slug)
    }
  }, [uebersicht, dbSlug])

  const { data: markdown, isFetching: markdownLaedt } = useQuery({
    queryKey: ["admin", "schema", appId, "db", dbSlug],
    queryFn: () => api.admin.schema.datenbank(appId!, dbSlug),
    enabled: appId !== undefined && dbSlug !== "" && tab === "struktur",
    staleTime: 5 * 60 * 1000,
  })

  const { data: historie } = useQuery({
    queryKey: ["admin", "schema", appId, "historie"],
    queryFn: () => api.admin.schema.historie(appId!),
    enabled: appId !== undefined && tab === "historie" && !eingeschraenkt,
  })

  const { data: analyse } = useQuery({
    queryKey: ["admin", "schema", appId, "analyse", analyseName],
    queryFn: () => api.admin.schema.analyse(appId!, analyseName),
    enabled: appId !== undefined && analyseName !== "",
    staleTime: Infinity,
  })

  // F30: Backup-Status (poll alle 10 s solange ein Lauf aktiv ist)
  const { data: backup, error: backupFehler } = useQuery({
    queryKey: ["admin", "schema-backup", "status"],
    queryFn: api.admin.schemaBackup.status,
    enabled: tab === "backup",
    refetchInterval: (query) => (query.state.data?.running ? 10_000 : false),
  })
  const { data: laeufe } = useQuery({
    queryKey: ["admin", "schema-backup", "runs"],
    queryFn: () => api.admin.schemaBackup.runs(),
    enabled: tab === "backup",
  })

  const backupStarten = useMutation({
    mutationFn: api.admin.schemaBackup.run,
    onSuccess: () => {
      toast.success("Backup-/Analyse-Lauf gestartet")
      void queryClient.invalidateQueries({ queryKey: ["admin", "schema-backup"] })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const tabellenAktualisieren = useMutation({
    mutationFn: () => api.admin.moduleAktualisieren(appId!),
    onSuccess: () => toast.success("Tabellen-Liste aus dem Schema aktualisiert"),
    onError: (e) => toast.error(apiError(e)),
  })

  const stand = useMemo(
    () => (uebersicht?.stand ? new Date(uebersicht.stand).toLocaleString("de-DE") : "—"),
    [uebersicht]
  )

  if (anwendungen && anwendungen.length === 0) {
    return <p className="text-muted-foreground">Keine aktive Anwendung konfiguriert.</p>
  }

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="mb-1 text-[26px] font-bold tracking-tight">Ninox-Schema</h1>
          <p className="text-sm text-muted-foreground">
            Struktur, Änderungs-Historie und Backup-Läufe —{" "}
            {uebersicht ? (
              <>
                Repo{" "}
                <a
                  href={uebersicht.repoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-primary hover:underline"
                >
                  {uebersicht.repo}
                </a>
                , Stand {stand}
              </>
            ) : (
              "lade …"
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => tabellenAktualisieren.mutate()}
            disabled={!appId || tabellenAktualisieren.isPending}
            title="Kuratierte Tabellen-Liste (Einordnung + Erfassung) neu aus dem Schema erzeugen"
            className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          >
            Tabellen-Liste aktualisieren
          </button>
          <button
            type="button"
            onClick={() => backupStarten.mutate()}
            disabled={backupStarten.isPending || backup?.running}
            title="F30: Schema-Backup + KI-Analyse jetzt ausführen"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${backup?.running ? "animate-spin" : ""}`} />
            {backup?.running ? "Lauf aktiv …" : "Jetzt aktualisieren"}
          </button>
        </div>
      </div>

      {uebersichtFehler != null && (
        <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {apiError(uebersichtFehler)}
        </p>
      )}
      {tabelleHinweis && (
        <p className="rounded-xl bg-primary/5 px-4 py-3 text-sm">
          Aus der Meldung verlinkt: Tabelle <code className="font-mono">{tabelleHinweis}</code> —
          im Dokument unten per Browser-Suche (Strg/Cmd-F) nach der ID suchen.
        </p>
      )}

      <div className="flex gap-1 border-b">
        {(
          [
            ["struktur", "Struktur-Browser"],
            ["historie", "Änderungs-Historie"],
            ["backup", "Backup-Läufe"],
          ] as const
        )
          .filter(([wert]) => wert !== "historie" || !eingeschraenkt)
          .map(([wert, label]) => (
          <button
            key={wert}
            type="button"
            onClick={() => setTab(wert)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-[13px] font-semibold transition-colors ${
              tab === wert
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "struktur" && (
        <div className="grid grid-cols-[240px_1fr] gap-4">
          <aside className={`${SEKTION} h-fit`}>
            <h2 className={TITEL}>Datenbanken ({uebersicht?.datenbanken.length ?? "…"})</h2>
            <div className="flex flex-col gap-1">
              {(uebersicht?.datenbanken ?? []).map((d) => (
                <button
                  key={d.slug}
                  type="button"
                  onClick={() => setDbSlug(d.slug)}
                  className={`truncate rounded-lg px-3 py-1.5 text-left text-[13px] font-medium transition-colors ${
                    dbSlug === d.slug
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-secondary"
                  }`}
                >
                  {d.slug}
                </button>
              ))}
            </div>
          </aside>
          <section className={SEKTION}>
            {markdownLaedt && <p className="text-sm text-muted-foreground">Lade Struktur …</p>}
            {markdown && <Markdown inhalt={markdown} />}
            {!markdownLaedt && !markdown && (
              <p className="text-sm text-muted-foreground">Datenbank links auswählen.</p>
            )}
          </section>
        </div>
      )}

      {tab === "historie" && (
        <div className="grid grid-cols-[minmax(320px,2fr)_3fr] items-start gap-4">
          <section className={SEKTION}>
            <h2 className={TITEL}>Schema-Commits</h2>
            <div className="flex flex-col gap-2.5">
              {(historie ?? []).map((c) => (
                <div key={c.sha} className="rounded-[10px] border px-3.5 py-2.5 text-[13px]">
                  <p className="font-semibold">{c.titel}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {c.datum ? new Date(c.datum).toLocaleString("de-DE") : "—"} ·{" "}
                    <code className="font-mono">{c.sha}</code> · {c.dateien.length} Datei(en)
                    {c.url && (
                      <>
                        {" · "}
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-0.5 font-semibold text-primary hover:underline"
                        >
                          Diff <ExternalLink className="h-3 w-3" />
                        </a>
                      </>
                    )}
                  </p>
                  {c.dateien.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-muted-foreground">
                        Geänderte Dateien
                      </summary>
                      <ul className="mt-1 list-disc pl-4 font-mono text-[11px] leading-5">
                        {c.dateien.map((d) => (
                          <li key={d}>{d}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
              {historie && historie.length === 0 && (
                <p className="text-sm text-muted-foreground">Keine Commits gefunden.</p>
              )}
            </div>
          </section>
          <section className={SEKTION}>
            <h2 className={TITEL}>KI-Analysen (F26)</h2>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {(uebersicht?.analysen ?? []).map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setAnalyseName(name)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    analyseName === name
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-secondary/80"
                  }`}
                >
                  {name.replace(".md", "")}
                </button>
              ))}
              {uebersicht && uebersicht.analysen.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Noch keine persistierten Analysen im Repo.
                </p>
              )}
            </div>
            {analyse && <Markdown inhalt={analyse} />}
          </section>
        </div>
      )}

      {tab === "backup" && (
        <div className="flex flex-col gap-4">
          <section className={SEKTION}>
            <h2 className={TITEL}>Dienst-Status</h2>
            {backupFehler != null ? (
              <p className="text-sm text-destructive">{apiError(backupFehler)}</p>
            ) : backup ? (
              <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-sm">
                <dt className="text-muted-foreground">Server</dt>
                <dd className="font-mono">{backup.server}</dd>
                <dt className="text-muted-foreground">Zeitplan</dt>
                <dd className="font-mono">{backup.schedule ?? "extern"}</dd>
                <dt className="text-muted-foreground">Zustand</dt>
                <dd>{backup.running ? "Lauf aktiv …" : "bereit"}</dd>
                <dt className="text-muted-foreground">Letzter Lauf</dt>
                <dd>
                  {backup.last_run
                    ? `${new Date(backup.last_run.finished_at).toLocaleString("de-DE")} — ${
                        backup.last_run.ok ? "OK" : `Fehler (Exit ${backup.last_run.exit_code})`
                      }`
                    : "—"}
                </dd>
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">Lade Status …</p>
            )}
          </section>
          <section className={SEKTION}>
            <h2 className={TITEL}>Letzte Läufe</h2>
            <div className="flex flex-col gap-2">
              {(laeufe ?? []).map((l) => (
                <details key={l.run_id} className="rounded-[10px] border px-3.5 py-2.5 text-[13px]">
                  <summary className="cursor-pointer">
                    <span className={`font-bold ${l.ok ? "text-primary" : "text-destructive"}`}>
                      {l.ok ? "OK" : `Fehler (${l.exit_code})`}
                    </span>{" "}
                    · {new Date(l.started_at).toLocaleString("de-DE")} ·{" "}
                    {l.trigger === "manual" ? "manuell" : "geplant"}
                  </summary>
                  <pre className="mt-2 overflow-x-auto rounded bg-muted/50 p-2.5 font-mono text-[11px] leading-4">
                    {l.log_tail || "(kein Log-Auszug)"}
                  </pre>
                </details>
              ))}
              {laeufe && laeufe.length === 0 && (
                <p className="text-sm text-muted-foreground">Noch keine Läufe protokolliert.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
